import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useMatch, useNavigate } from 'react-router-dom';
import { ArrowLeft, Paintbrush } from 'lucide-react';
import { Button, Pill, PillGroup } from '@/components/ui';
import { useReviewQueueTotal } from '@/hooks/useReviewQueue';
import { embedLayout } from '@/lib/embed-layout';
import { cn } from '@/lib/utils';
import {
  endImpersonationSession,
  isImpersonationTab,
} from '@/lib/impersonation-session';
import { isEmbedded, useAuthStore, useDesignStore, useUIStore } from '@/stores';

/**
 * Frame of the seller area (docs/design/seller-area/SellerShell.dc.html):
 * a 64 px header with the wordmark, the three section pills and, on the
 * right, the way back into an open design. "Log out" exists only at top
 * level — inside the shop's iframe the shop owns the session.
 */
export function SellerShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const isIframe = useAuthStore((state) => state.isIframe);
  const designId = useDesignStore((state) => state.designId);
  const currentStep = useUIStore((state) => state.currentStep);
  const { data: reviewTotal = 0 } = useReviewQueueTotal();

  // Subscribed to `isIframe` above, so a flag change re-evaluates this.
  const embedded = isIframe && isEmbedded();
  // The host frames the page (Shopify admin): its title bar and navigation
  // replace ours, so only the page itself is rendered.
  const { hostChrome, hideBrand } = embedLayout();
  const creatorPath = designId
    ? `/creator/${designId}?step=${currentStep}`
    : null;

  const handleLogout = async () => {
    // An impersonation tab has no login page: logging out ends the
    // impersonation on its end screen and leaves the admin's session alone.
    if (isImpersonationTab()) {
      endImpersonationSession('ended');
      return;
    }
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  if (hostChrome) {
    return (
      <div className="flex min-h-screen flex-col bg-paper">
        <main className="flex flex-1 flex-col px-4 py-3">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen flex-col bg-paper"
      // Clears the fixed impersonation banner; the variable is absent (0px)
      // in every other tab.
      style={{ paddingTop: 'var(--impersonation-banner-height, 0px)' }}
    >
      <header className="flex h-16 shrink-0 items-center gap-6 border-b border-line bg-paper px-8">
        {/* Inside a Seller iframe the shop names the app; ?logo=show
            brings the wordmark back (lib/embed-layout.ts). */}
        {!hideBrand && (
          <Link
            to="/designs"
            aria-label={t('seller.shell.wordmark')}
            className="flex items-center gap-2.5 rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
          >
            <span
              className="flex h-8 w-8 items-center justify-center rounded-control bg-primary text-primary-fg"
              aria-hidden="true"
            >
              <Paintbrush className="h-[18px] w-[18px]" />
            </span>
            <span className="font-display text-lg font-bold tracking-[-0.02em] text-ink">
              DesignDeck
            </span>
          </Link>
        )}

        <nav aria-label={t('seller.shell.navLabel')}>
          <PillGroup mode="nav" className="flex-nowrap">
            <NavPill to="/designs" label={t('seller.shell.nav.designs')} />
            <NavPill
              to="/review"
              label={t('seller.shell.nav.review')}
              count={reviewTotal}
              countLabel={t('seller.shell.reviewCount', {
                count: reviewTotal,
              })}
            />
            <NavPill
              to="/connections"
              label={t('seller.shell.nav.connections')}
            />
            <NavPill
              to="/shop-preview"
              label={t('seller.shell.nav.shopPreview')}
            />
          </PillGroup>
        </nav>

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          {creatorPath && (
            <Button asChild variant="ghost" size="sm" className="text-ink-2">
              <Link to={creatorPath}>
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                {t('seller.shell.nav.backToCreator')}
              </Link>
            </Button>
          )}
          {!embedded && (
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              {t('seller.shell.nav.logout')}
            </Button>
          )}
        </div>
      </header>

      <main className="flex flex-1 flex-col px-8 py-9">
        <Outlet />
      </main>
    </div>
  );
}

interface NavPillProps {
  to: string;
  label: string;
  /** Badge value; nothing is shown at 0. */
  count?: number;
  /** Spoken form of the badge (`seller.shell.reviewCount`). */
  countLabel?: string;
}

/** One section pill: a router link in the kit's pill face. */
function NavPill({ to, label, count = 0, countLabel }: NavPillProps) {
  const active = useMatch({ path: to, end: false }) !== null;

  return (
    <Pill asChild active={active}>
      <NavLink to={to}>
        {label}
        {count > 0 && (
          <span
            className={cn(
              'inline-flex h-5 min-w-5 items-center justify-center rounded-chip px-1.5 font-mono text-[11px] font-medium',
              active ? 'bg-primary-deep text-primary-fg' : 'bg-paper text-ink'
            )}
          >
            <span aria-hidden="true">{count}</span>
            <span className="sr-only">{countLabel}</span>
          </span>
        )}
      </NavLink>
    </Pill>
  );
}
